import React, { useState } from "react";
import { useNavigate } from "react-router";
import { Lock, Eye, EyeOff, ChevronRight, Loader2 } from "lucide-react";
import { useAdminAuth, type AdminRole } from "../auth/AdminAuthContext";
import { isApiEnabled } from "../../api/config";
import { ApiError } from "../../api/client";

export function AdminLoginPage() {
  const { loginWithPassword } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const routeAfterLogin = (
    role: AdminRole,
    assignedEventId?: string,
    eventAccessCount = 0,
  ) => {
    if (role === "super_admin") {
      navigate("/");
      return;
    }
    if (role === "curator") {
      // Multi-event organizers: always land on the full list, not a single “nearest” show.
      if (eventAccessCount > 0 || assignedEventId) {
        navigate("/events");
        return;
      }
      setError("Нет назначенных мероприятий. Обратитесь к администратору платформы.");
      return;
    }
    if (role === "exhibitor" && assignedEventId) {
      navigate("/exhibitor");
      return;
    }
    setError("Нет доступа к кабинету. Проверьте роль или обратитесь в поддержку.");
  };

  const handleLogin = async () => {
    setError("");
    if (!email.trim() || password.length < 3) {
      setError("Введите email/телефон и пароль");
      return;
    }

    setLoading(true);
    try {
      if (isApiEnabled()) {
        await loginWithPassword(email.trim(), password);
        const stored = localStorage.getItem("mvk_admin_profile");
        if (stored) {
          const user = JSON.parse(stored);
          const accessCount = Array.isArray(user.eventAccess) ? user.eventAccess.length : 0;
          routeAfterLogin(user.role, user.assignedEventId, accessCount);
        } else {
          navigate("/");
        }
      } else {
        setError("API недоступен. Запустите pnpm dev и docker compose.");
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Неверный логин или пароль");
      } else {
        setError("Не удалось войти. Проверьте API и миграции.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img
            src="/icons/app-icon.png?v=20260916"
            alt="MVK One"
            width={48}
            height={48}
            className="mx-auto mb-3 h-12 w-12 rounded-2xl shadow-sm"
          />
          <h1 className="text-[22px] text-gray-900">MVK One</h1>
          <p className="text-[14px] text-gray-500 mt-1">Личный кабинет участника выставки</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <div>
            <label className="text-[12px] text-gray-500 mb-1.5 block">Email или телефон</label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@company.ru или +79001234567"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-[14px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              autoComplete="username"
            />
          </div>

          <div>
            <label className="text-[12px] text-gray-500 mb-1.5 block flex items-center gap-1">
              <Lock size={12} /> Пароль
            </label>
            <div className="relative">
              <input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void handleLogin()}
                placeholder="••••••••"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-[14px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && <p className="text-[12px] text-red-500">{error}</p>}

          <button
            onClick={() => void handleLogin()}
            disabled={loading}
            className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-[14px] transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            Войти <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
